import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {ClientPanelApi} from '../../../api/clientPanelApi';
import {Spinner} from '../../Spinner';
import {PlatformsBox} from '../control_central/PlatformsBox';
import {QuickConnectAccountModal} from '../control_central/QuickConnectAccountModal';
import {AddClientStoreModal} from '../ad_accounts/AddClientStoreModal';
import {renderAccountRows} from './AccountRows';
import {MissingPlatformsNotice} from './MissingPlatformsNotice';
import {PLATFORMS_CARD_ID} from './panelAnchors';
import {anchorAttr} from '../../../guides/anchors';
import {CHANNEL_INFO_BY_CHANNEL, CHANNEL_TYPE_ECOMMERCE} from '../../channels/channels';
import '../control_central/ControlCentral.scss';
import './MainCards.scss';

/**
 * Card de Plataformas del Panel V11 — un envoltorio ALREDEDOR del `PlatformsBox` de la ficha
 * clásica, no una card nueva.
 *
 * El bloque `panel.platforms` del agregado viene con los MISMOS shapes que `GET client/:id`
 * justamente para poder reusar ese componente sin adaptador: la cabecera, las filas y el
 * "Ver todas las integraciones" los sigue poniendo él, así que un cambio en la ficha vieja llega
 * gratis a las dos pantallas (y no hay dos verdades sobre qué está conectado).
 *
 * Lo que sí vive acá adentro son los handlers, calcados de `ControlCentralContent`: conectar, y
 * ignorar / dejar de ignorar un canal. Diferencia con la ficha vieja: al éxito de cualquiera se
 * refresca EL AGREGADO entero (`onRefetch`) — conectar una cuenta cambia el header, los primeros
 * pasos y los desbloqueos de la IA, no sólo esta card.
 */
export function PlatformsCard({data, loading, clientId, clientName, onRefetch}) {
    const {t} = useTranslation();
    // El canal que está conectándose: es a la vez el "abierto" del modal y su parámetro.
    const [connectingChannel, setConnectingChannel] = useState(null);
    const liveSync = useHistoricalSyncProgress(clientId, data);

    const refetch = () => {
        if (onRefetch) onRefetch();
    };

    // Genérico a propósito: el `error_message` del backend puede nombrar otra agencia ("esa cuenta
    // ya está conectada a…") y eso no se muestra jamás (aislamiento cross-agency).
    const notifyError = () => {
        toast.error(t('client_panel.platforms_action_error',
            'No pudimos actualizar la integración. Intenta de nuevo en unos segundos.'));
    };

    // Una tienda se vincula en su propio modal, con el cliente ya fijo (card fpjHztLO): ahí se elige
    // una tienda que la agencia ya conectó o se conecta una nueva. Antes se salía a la pantalla de
    // cuentas del cliente. El modal sólo se abre por `toggle()` (no tiene `isOpen`), así que va por
    // ref; el canal viaja por estado para que el formulario abra en la plataforma elegida.
    const storeModal = useRef(null);
    const [storeChannel, setStoreChannel] = useState(null);
    const openStoreModal = useRef(false);
    useEffect(() => {
        if (openStoreModal.current && storeModal.current) {
            openStoreModal.current = false;
            storeModal.current.toggle();
        }
    }, [storeChannel]);

    const handleConnectChannel = (channel) => {
        const info = CHANNEL_INFO_BY_CHANNEL[channel];
        if (info && info.channelType === CHANNEL_TYPE_ECOMMERCE) {
            openStoreModal.current = true;
            // Mismo canal dos veces seguidas: el efecto no corre, se abre directo.
            if (storeChannel === channel) {
                openStoreModal.current = false;
                if (storeModal.current) storeModal.current.toggle();
                return;
            }
            setStoreChannel(channel);
            return;
        }
        setConnectingChannel(channel);
    };

    const postChannelAction = (path, channel, onSuccess) => {
        new APIClient(`${API_URL}client/${clientId}/${path}`).call('POST', {channel: channel}).then((response) => {
            if (response && response.status === 'OK') {
                refetch();
                if (onSuccess) onSuccess();
            } else {
                notifyError();
            }
        });
    };

    const handleIgnoreChannel = (channel) => postChannelAction('ignore-channel/', channel);

    // Dejar de ignorar es siempre el primer paso de "lo quiero conectar": encadenar el connect
    // ahorra el segundo click (mismo comportamiento que la ficha clásica).
    const handleUnignoreChannel = (channel) => postChannelAction(
        'unignore-channel/', channel, () => handleConnectChannel(channel));

    // Los modales van FUERA de la rama del spinner, siempre en el mismo lugar del árbol. «Dejar de
    // ignorar» una tienda refresca el Panel (`loading` pasa a true) en el mismo commit en que abre el
    // modal de tienda: si el modal viviera sólo en la rama con datos, el spinner lo desmontaría y
    // perdería su «abierto», que es estado interno del modal (review C-CUENTAS-01).
    let body = null;
    if (loading) {
        // El spinner también lleva el id: el «Vincular cuenta» del header tiene adónde bajar
        // mientras el Panel carga.
        body = (
            <div className="box pnl-card" id={PLATFORMS_CARD_ID}>
                <div className="box-loader"><Spinner color="positive small"/></div>
            </div>
        );
    } else if (data) {
        body = (
            <PlatformsBox
                clientId={clientId}
                clientAdAccounts={data.client_ad_accounts}
                clientStores={data.client_stores}
                channels={data.channels}
                ignoredChannels={data.ignored_channels}
                businessType={data.business_type}
                onConnectChannel={handleConnectChannel}
                onIgnoreChannel={handleIgnoreChannel}
                onUnignoreChannel={handleUnignoreChannel}
                manageLabel={t('client_panel.platforms_manage', 'Vincular fuente')}
                // `renderAccountRows` devuelve `null` —y no un elemento— para los canales SIN
                // cuentas: ahí la fila tiene que caer al resumen de siempre. Los canales conectados
                // muestran cuenta por cuenta su moneda, su conversión, su límite diario (donde
                // aplica) y si Dashbo la sigue pudiendo leer.
                renderAccountsDetail={(channel, accounts) => renderAccountRows(
                    channel, accounts, refetch, liveSync)}
                // GA4 / la tienda / la pauta que faltan (RNAjmiW6): mismo `handleConnectChannel`
                // que las filas, así cada botón abre el modal que corresponde.
                renderMissingNotice={(missing) => (
                    <MissingPlatformsNotice missing={missing} clientId={clientId}
                                            onConnect={handleConnectChannel}/>
                )}
                // Sumar otra cuenta a un canal que ya tiene es el mismo flujo que la primera.
                onAddAccount={handleConnectChannel}
                // Destino del «Vincular cuenta» del header del Panel (`PanelHeader`).
                id={PLATFORMS_CARD_ID}
                guideAnchor={anchorAttr('panel_data_sources')}
            />
        );
    }

    return (
        <>
            {body}

            <QuickConnectAccountModal
                isOpen={!!connectingChannel}
                toggle={() => setConnectingChannel(null)}
                channel={connectingChannel}
                clientId={clientId}
                onSuccess={refetch}
                linkedAccountIds={linkedAccountIds(data, connectingChannel)}
            />

            <AddClientStoreModal
                ref={storeModal}
                clientId={clientId}
                clientName={clientName}
                channel={storeChannel || undefined}
                afterSuccessfulSubmit={refetch}
            />
        </>
    );
}

/**
 * Refresca en vivo la preparación del histórico, mientras haya alguna cuenta preparándose.
 *
 * La preparación tarda **horas**: una barra que sólo se mueve cuando el usuario recarga la página
 * no cumple lo que promete. Pero refrescar el agregado entero (`onRefetch`) cada minuto son ~65
 * queries por pestaña abierta para actualizar una línea de texto, así que se pide sólo el progreso
 * a su endpoint propio, que cuesta 3.
 *
 * Tres reglas que evitan que esto se vuelva un goteo permanente contra el backend:
 *
 * 1. **Sólo se poletea si hay algo que mirar.** El disparador es el agregado: si ninguna cuenta
 *    vino preparándose, el intervalo ni se arma. Y el primer tick que devuelve "ninguna" lo apaga.
 * 2. **Con la pestaña oculta no se pide nada.** Un usuario que dejó el Panel abierto en una solapa
 *    de fondo no necesita que la barra avance; al volver se pide una vez enseguida, para que no vea
 *    un estado viejo mientras espera el próximo tick.
 * 3. **Un error no borra lo que está en pantalla**: se conserva el último estado conocido y se
 *    reintenta en el tick siguiente. Es información de acompañamiento, no vale un cartel de error.
 *
 * Devuelve `null` mientras no llegó ningún refresco (ahí manda lo que trajo el agregado) o el mapa
 * `{'<ad_account_id>': {...}}` del último tick — que puede ser `{}`, y eso significa "ya terminó
 * todo", no "todavía no sé".
 */
function useHistoricalSyncProgress(clientId, data) {
    const [liveSync, setLiveSync] = useState(null);

    // El agregado se volvió a pedir (se conectó una cuenta, se refrescó el Panel): su foto es la
    // más nueva y vuelve a ser la fuente, hasta que llegue el próximo tick.
    useEffect(() => setLiveSync(null), [data]);

    const somethingIsPreparing = liveSync
        ? Object.keys(liveSync).length > 0
        : accountsPreparing(data).length > 0;

    const fetchProgress = useCallback(() => ClientPanelApi.getPlatformsSyncProgress(clientId)
        .then((response) => {
            // `status !== 'OK'` y no `=== 'ERROR'`: el backend manda el código tipado en `status`,
            // así que comparar contra 'ERROR' se come en silencio todo lo demás.
            if (!response || response.status !== 'OK' || !response.data) return null;
            return response.data.accounts || {};
        })
        .catch(() => null), [clientId]);

    useEffect(() => {
        if (!somethingIsPreparing) return undefined;

        let cancelled = false;
        const apply = (accounts) => {
            if (cancelled || accounts === null) return;
            setLiveSync(accounts);
        };
        const tick = () => {
            if (typeof document !== 'undefined' && document.hidden) return;
            fetchProgress().then(apply);
        };

        const interval = setInterval(tick, SYNC_POLL_MS);
        const onVisible = () => {
            if (typeof document !== 'undefined' && !document.hidden) tick();
        };
        if (typeof document !== 'undefined' && document.addEventListener) {
            document.addEventListener('visibilitychange', onVisible);
        }

        return () => {
            cancelled = true;
            clearInterval(interval);
            if (typeof document !== 'undefined' && document.removeEventListener) {
                document.removeEventListener('visibilitychange', onVisible);
            }
        };
    }, [somethingIsPreparing, fetchProgress]);

    return liveSync;
}

// Un mes de histórico tarda varios minutos en traerse: pedirlo más seguido no haría avanzar la
// barra más rápido, sólo sumaría requests.
const SYNC_POLL_MS = 60000;

/** Los `account_id` que el cliente ya tiene en ese canal: el modal no los vuelve a ofrecer. */
function linkedAccountIds(data, channel) {
    if (!data || !channel) return [];
    return (data.client_ad_accounts || [])
        .filter((account) => account.channel === channel)
        .map((account) => String(account.account_id));
}

/** Las cuentas que el AGREGADO trajo preparándose — tiendas incluidas, que viajan aparte. */
function accountsPreparing(data) {
    if (!data) return [];
    const all = (data.client_ad_accounts || []).concat(data.client_stores || []);
    return all.filter((account) => !!account.historical_sync);
}
